import React, { useEffect, useMemo, useState } from 'react'
import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import api from '../../../services/authService'
import { obtenerMensajeErrorApi } from '../../../services/apiError'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'

interface Organizacion {
  id_organizacion: number
  slug: string
  nombre: string
  tipo: string
  subtipo?: string | null
  descripcion?: string | null
  contacto_email?: string | null
  contacto_telefono?: string | null
  activo: boolean
}

interface GrupoEmpresa {
  id_grupo: number
  nombre: string
  descripcion?: string | null
  tipo_grupo?: string | null
  activo: boolean
  miembros_count: number
}

interface MiembroEmpresa {
  id_miembro: number
  id_usuario: number
  nombre: string
  apellido?: string | null
  email: string
  rol_global: string
  rol_miembro: string
  estado: string
  turno?: string | null
  departamento?: string | null
}

interface DashboardGestionEmpresa {
  vista: 'gestion'
  organizacion: Organizacion
  metricas: {
    total_miembros: number
    miembros_activos: number
    total_grupos: number
    total_equipos: number
    total_usuarios_operativos: number
    total_supervisores: number
    total_contadores: number
  }
  grupos: GrupoEmpresa[]
  miembros: MiembroEmpresa[]
}

interface AsignacionEmpresa {
  id_asignacion: number
  id_grupo: number
  nombre_grupo: string
  id_simulacion: number
  titulo: string
  descripcion?: string | null
  tipo: string
  fecha_limite?: string | null
  estado: string
  progreso: number
  puntaje?: number | null
}

interface DashboardUsuarioEmpresa {
  vista: 'usuario'
  organizacion: Organizacion
  grupos: GrupoEmpresa[]
  mis_asignaciones: AsignacionEmpresa[]
  metricas: {
    total_grupos: number
    total_asignaciones: number
    activas: number
    completadas: number
  }
}

type DashboardEmpresa = DashboardGestionEmpresa | DashboardUsuarioEmpresa

const PaginaPanelEmpresa: React.FC = () => {
  const { usuario } = useAuth()
  const [dashboard, setDashboard] = useState<DashboardEmpresa | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const esGestion = dashboard?.vista === 'gestion'
  const organizacion = dashboard?.organizacion

  const grupos = useMemo(() => dashboard?.grupos || [], [dashboard])
  const miembros = useMemo(() => (dashboard?.vista === 'gestion' ? dashboard.miembros : []), [dashboard])
  const asignaciones = useMemo(() => (dashboard?.vista === 'usuario' ? dashboard.mis_asignaciones : []), [dashboard])

  useEffect(() => {
    const cargarDashboard = async () => {
      if (typeof usuario?.id_organizacion !== 'number') {
        setDashboard(null)
        setError('No se encontró una organización activa para cargar el panel empresarial.')
        return
      }

      setLoading(true)
      setError(null)
      try {
        const respuesta = await api.get<DashboardEmpresa>('/api/organizaciones/mi-dashboard')
        setDashboard(respuesta.data)
      } catch (err) {
        setError(obtenerMensajeErrorApi(err, 'No se pudo cargar el panel empresarial'))
      } finally {
        setLoading(false)
      }
    }

    cargarDashboard()
  }, [usuario?.id_organizacion])

  const formatearRol = (rol: string) =>
    rol
      .replace('empresa_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())

  return (
    <div className="space-y-6">
      <section className="rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-6 text-white shadow-lg">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-emerald-100">Modulo Empresarial</p>
            <h1 className="mt-2 text-2xl font-bold md:text-3xl">{organizacion?.nombre || 'Empresa'}</h1>
            <p className="mt-2 max-w-2xl text-sm text-emerald-50">
              {organizacion?.descripcion || 'Dashboard canónico sobre el dominio organizacional para operaciones, supervisión y seguimiento empresarial.'}
            </p>
          </div>
          <div className="rounded-2xl bg-white/10 px-4 py-3 text-sm">
            <p><span className="font-semibold">Tipo:</span> {organizacion?.tipo || 'empresa'}</p>
            <p><span className="font-semibold">Subtipo:</span> {organizacion?.subtipo || 'general'}</p>
            <p><span className="font-semibold">Estado:</span> {organizacion?.activo ? 'Activa' : 'Inactiva'}</p>
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          Cargando dashboard empresarial...
        </div>
      ) : null}

      {!loading && dashboard && esGestion && (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Miembros</span>
                <UserGroupIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">{dashboard.metricas.total_miembros}</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{dashboard.metricas.miembros_activos} activos</p>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Equipos</span>
                <BuildingOffice2Icon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
              </div>
              <p className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">{dashboard.metricas.total_equipos}</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Grupos organizacionales activos</p>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Supervisión</span>
                <ChartBarIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">{dashboard.metricas.total_supervisores}</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Supervisores registrados</p>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Contabilidad</span>
                <ClipboardDocumentListIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <p className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">{dashboard.metricas.total_contadores}</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Usuarios con rol contador</p>
            </article>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Miembros de la empresa</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Nombre</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Departamento</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {miembros.map((miembro) => (
                      <tr key={miembro.id_miembro}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">{`${miembro.nombre} ${miembro.apellido || ''}`.trim()}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{miembro.email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatearRol(miembro.rol_miembro)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{miembro.departamento || 'Sin asignar'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${miembro.estado === 'activo' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                            {miembro.estado}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {miembros.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          No hay miembros registrados en esta organización.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Equipos y áreas</h2>
              </div>
              <div className="space-y-3 p-4">
                {grupos.map((grupo) => (
                  <div key={grupo.id_grupo} className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{grupo.nombre}</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{grupo.descripcion || 'Sin descripción registrada'}</p>
                      </div>
                      <span className="rounded-full bg-teal-100 px-2.5 py-1 text-xs font-semibold text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                        {grupo.miembros_count} miembros
                      </span>
                    </div>
                    <p className="mt-3 text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      {grupo.tipo_grupo || 'equipo'}
                    </p>
                  </div>
                ))}
                {grupos.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    No hay equipos o áreas registrados todavía.
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {!loading && dashboard && dashboard.vista === 'usuario' && (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Mis grupos</span>
                <UserGroupIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">{dashboard.metricas.total_grupos}</p>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Asignaciones</span>
                <BriefcaseIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
              </div>
              <p className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">{dashboard.metricas.total_asignaciones}</p>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Activas</span>
                <ChartBarIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">{dashboard.metricas.activas}</p>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Completadas</span>
                <ClipboardDocumentListIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <p className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">{dashboard.metricas.completadas}</p>
            </article>
          </section>

          <section className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Mis equipos</h2>
              <div className="mt-4 space-y-3">
                {grupos.map((grupo) => (
                  <div key={grupo.id_grupo} className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <p className="font-medium text-gray-900 dark:text-white">{grupo.nombre}</p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{grupo.descripcion || 'Sin descripción registrada'}</p>
                  </div>
                ))}
                {grupos.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No tienes grupos asignados.</p>}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Mis asignaciones</h2>
              </div>
              <div className="space-y-4 p-4">
                {asignaciones.map((asignacion) => (
                  <div key={asignacion.id_asignacion} className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{asignacion.titulo}</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{asignacion.descripcion || 'Sin descripción disponible'}</p>
                      </div>
                      <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        {asignacion.estado}
                      </span>
                    </div>
                    <div className="mt-4 grid gap-3 text-sm text-gray-600 dark:text-gray-300 md:grid-cols-4">
                      <p><span className="font-medium">Grupo:</span> {asignacion.nombre_grupo}</p>
                      <p><span className="font-medium">Tipo:</span> {asignacion.tipo}</p>
                      <p><span className="font-medium">Progreso:</span> {asignacion.progreso}%</p>
                      <p><span className="font-medium">Puntaje:</span> {asignacion.puntaje ?? 'Pendiente'}</p>
                    </div>
                  </div>
                ))}
                {asignaciones.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    No hay asignaciones disponibles para tu usuario.
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  )
}

export default PaginaPanelEmpresa
